// src/features/secureDocuments/services/SecureDocumentService.ts

import bcrypt from 'bcryptjs';
import { supabase } from '../../../shared/services/supabase';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { berlinLocalToUtcIso } from '../../../shared/utils/berlinTime';
import {
  SecureCustomerUpload,
  SecureCustomerUploadFile,
  SecureUploadWithFiles,
  SecureUploadCreateInput,
  SecureUploadResult,
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE
} from '../types';

/**
 * NEU (Zugriffsprotokoll): Ein Eintrag im Zugriffsprotokoll des
 * Mandanten-Portals. Geschrieben werden die Einträge ausschließlich
 * serverseitig von den Netlify Functions secure-customer-access
 * (event_type 'login') und secure-customer-download (event_type
 * 'download', mit file_name). Das Frontend liest nur.
 */
export interface SecureAccessLogEntry {
  id: string;
  contact_id: string;
  event_type: 'login' | 'download';
  file_name: string | null;
  created_at: string;
}

/**
 * Stufe 2: Optionale Erweiterungen für createUploadWithFiles —
 * Außenidentität „Senden als" und terminierter Versand. Ohne options ist
 * das Verhalten von createUploadWithFiles exakt der Bestand.
 */
export interface SecureUploadSendOptions {
  /** Anzeigename des gewählten Absenders — bestimmt den kundensichtbaren Titel. */
  senderName?: string;
  /**
   * E-Mail des gewählten Absenders — wird als sender_email persistiert
   * (steuert Signatur/Absendername/Duz-Gate der Versand-Mail; Grundlage
   * für den terminierten Versand durch den Cron secure-scheduled-send).
   */
  senderEmail?: string;
  /**
   * Terminierter Versand: Berliner Wanduhrzeit "YYYY-MM-DDTHH:mm". Wenn
   * gesetzt, wird das Paket mit versand_status='geplant' angelegt — der
   * Aufrufer darf dann KEINE Sofort-Mail auslösen; der Cron übernimmt zum
   * Zeitpunkt.
   */
  scheduledAtLocal?: string | null;
  /**
   * Kundensichtbare Überschrift des Pakets. Wenn gesetzt (nicht leer), wird
   * dieser Titel verwendet; ist er leer/undefiniert, bleibt die automatische
   * Titel-Erzeugung ("Datei Upload von {Absender} am {Datum}") als Fallback
   * exakt erhalten.
   */
  titel?: string;
  /**
   * Punkt 2 (Veralten-Tracking): Periode + Paket-Typ des Pakets. Werden an
   * createUpload durchgereicht und bilden den Gruppierungsschlüssel für die
   * automatische Veraltet-Markierung. Ohne diese Felder unverändertes Verhalten.
   */
  periodeJahr?: number | null;
  periodeMonat?: number | null;
  paketTyp?: string | null;
}

/**
 * Service für gesicherten Dokumentenversand an Kunden
 * 
 * Unterschied zum CommunicationDocumentService:
 * - Ein Token pro Ansprechpartner (nicht pro Dokument)
 * - Mehrere Dateien pro Upload
 * - Download-Seite zeigt alle Uploads chronologisch
 * - Jeder Ansprechpartner hat einen eigenen, isolierten Downloadbereich
 */
export class SecureDocumentService {

  // ============================================
  // PRIVATE HELPER METHODEN
  // ============================================

  /**
   * Generiert einen kryptographisch sicheren Token
   * 64 Hex-Zeichen = 256 Bit Entropie (nicht erratbar)
   */
  private generateToken(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Hasht ein Passwort mit bcrypt
   */
  private async hashPassword(passwort: string): Promise<string> {
    const salt = await bcrypt.genSalt(10);
    return bcrypt.hash(passwort, salt);
  }

  /**
   * Ermittelt den Dateityp aus dem MIME-Type
   */
  private getFileTypeFromMime(mimeType: string): string | null {
    return ALLOWED_MIME_TYPES[mimeType] || null;
  }

  /**
   * Bereinigt einen Dateinamen für Supabase Storage
   * Ersetzt Umlaute, Sonderzeichen und Leerzeichen
   */
  private sanitizeFilename(filename: string): string {
    const umlautMap: Record<string, string> = {
      'ä': 'ae', 'ö': 'oe', 'ü': 'ue',
      'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue',
      'ß': 'ss'
    };
    
    let sanitized = filename;
    
    // Umlaute ersetzen
    for (const [umlaut, replacement] of Object.entries(umlautMap)) {
      sanitized = sanitized.split(umlaut).join(replacement);
    }
    
    // Leerzeichen durch Unterstriche ersetzen
    sanitized = sanitized.replace(/\s+/g, '_');
    
    // Alle anderen Sonderzeichen entfernen (außer Punkt, Bindestrich, Unterstrich)
    sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '');
    
    return sanitized;
  }

  /**
   * Validiert eine Datei vor dem Upload
   */
  private validateFile(file: File): { valid: boolean; error?: string; fileType?: string } {
    // Größe prüfen
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `Datei "${file.name}" ist zu groß (max. 2 GB)`
      };
    }

    // Dateityp prüfen
    const fileType = this.getFileTypeFromMime(file.type);
    if (!fileType) {
      return {
        valid: false,
        error: `Dateityp "${file.type}" wird nicht unterstützt`
      };
    }

    return { valid: true, fileType };
  }

  // ============================================
  // KUNDEN-TOKEN VERWALTUNG (Legacy - für Abwärtskompatibilität)
  // ============================================

  /**
   * Stellt sicher, dass ein Kunde einen Download-Token hat.
   * Generiert einen neuen Token falls keiner existiert.
   * 
   * HINWEIS: Im neuen Multi-Contact-Flow wird stattdessen
   * customerContactService.ensureContactToken() verwendet.
   */
  async ensureCustomerToken(customerId: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      // Prüfe ob Token existiert
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('secure_download_token')
        .eq('id', customerId)
        .single();

      if (fetchError) {
        return {
          success: false,
          error: `Kunde nicht gefunden: ${fetchError.message}`
        };
      }

      // Token existiert bereits
      if (customer.secure_download_token) {
        return {
          success: true,
          token: customer.secure_download_token
        };
      }

      // Neuen Token generieren
      const newToken = this.generateToken();

      const { error: updateError } = await supabase
        .from('customers')
        .update({ secure_download_token: newToken })
        .eq('id', customerId);

      if (updateError) {
        return {
          success: false,
          error: `Token konnte nicht gespeichert werden: ${updateError.message}`
        };
      }

      console.log(`[SecureDocumentService] Generated new token for customer ${customerId}`);

      return {
        success: true,
        token: newToken
      };

    } catch (error) {
      console.error('Error in ensureCustomerToken:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Prüft ob ein Kunde ein Download-Passwort gesetzt hat
   */
  async hasCustomerPassword(customerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('secure_download_passwort')
        .eq('id', customerId)
        .single();

      if (error || !data) return false;

      return !!data.secure_download_passwort && data.secure_download_passwort.trim() !== '';
    } catch {
      return false;
    }
  }

  // ============================================
  // UPLOAD VERWALTUNG
  // ============================================

  /**
   * Erstellt einen neuen Upload-Eintrag (ohne Dateien)
   */
  async createUpload(input: SecureUploadCreateInput): Promise<SecureUploadResult> {
    try {
      const { data, error } = await supabase
        .from('secure_customer_uploads')
        .insert({
          customer_id: input.customer_id,
          contact_id: input.contact_id,
          titel: input.titel,
          kommentar: input.kommentar || null,
          erstellt_von_email: input.erstellt_von_email,
          erstellt_von_name: input.erstellt_von_name,
          // Stufe 2: terminierter Versand + Außenidentität „Senden als".
          // Ohne die optionalen Input-Felder ist der Insert byte-identisch
          // zum Bestand (versand_status 'entwurf', keine Zusatzspalten).
          versand_status: input.geplanter_versand_am ? 'geplant' : 'entwurf',
          ...(input.sender_email !== undefined && input.sender_email !== null
            ? { sender_email: input.sender_email }
            : {}),
          ...(input.geplanter_versand_am
            ? { geplanter_versand_am: input.geplanter_versand_am }
            : {}),
          // Punkt 2 (Veralten-Tracking): Periode + Paket-Typ nur setzen, wenn
          // vorhanden — sonst Insert byte-identisch zum Bestand.
          ...(input.periode_jahr !== undefined && input.periode_jahr !== null
            ? { periode_jahr: input.periode_jahr }
            : {}),
          ...(input.periode_monat !== undefined && input.periode_monat !== null
            ? { periode_monat: input.periode_monat }
            : {}),
          ...(input.paket_typ !== undefined && input.paket_typ !== null
            ? { paket_typ: input.paket_typ }
            : {})
        })
        .select('*')
        .single();

      if (error) {
        console.error('Error creating upload:', error);
        return {
          success: false,
          error: `Upload erstellen fehlgeschlagen: ${error.message}`
        };
      }

      return {
        success: true,
        upload: data as SecureCustomerUpload
      };

    } catch (error) {
      console.error('Error in createUpload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Lädt eine Datei in den Storage und erstellt den DB-Eintrag
   */
  async uploadFile(
    uploadId: string,
    customerId: string,
    file: File,
    sortOrder: number = 0
  ): Promise<{ success: boolean; fileId?: string; error?: string }> {
    try {
      // Validierung
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Dateiname für Storage bereinigen
      const sanitizedFilename = this.sanitizeFilename(file.name);

      // Storage-Pfad: {customer_id}/{upload_id}/{sanitized_filename}
      const storagePath = `${customerId}/${uploadId}/${sanitizedFilename}`;

      // In Supabase Storage hochladen
      const { error: storageError } = await supabase.storage
        .from('secure-customer-documents')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (storageError) {
        console.error('Storage upload error:', storageError);
        return {
          success: false,
          error: `Datei hochladen fehlgeschlagen: ${storageError.message}`
        };
      }

      // DB-Eintrag erstellen
      const { data, error: dbError } = await supabase
        .from('secure_customer_upload_files')
        .insert({
          upload_id: uploadId,
          original_filename: file.name,
          storage_path: storagePath,
          file_size: file.size,
          file_type: validation.fileType,
          sort_order: sortOrder
        })
        .select('id')
        .single();

      if (dbError) {
        // Rollback: Datei aus Storage löschen
        await supabase.storage
          .from('secure-customer-documents')
          .remove([storagePath]);

        console.error('DB insert error:', dbError);
        return {
          success: false,
          error: `Datei-Eintrag erstellen fehlgeschlagen: ${dbError.message}`
        };
      }

      return {
        success: true,
        fileId: data.id
      };

    } catch (error) {
      console.error('Error in uploadFile:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Datei-Upload'
      };
    }
  }

  /**
   * Kompletter Upload-Flow: Erstellt Upload + lädt alle Dateien hoch
   * Nutzt den Ansprechpartner-Token für isolierten Downloadbereich
   *
   * options (Stufe 2, optional — ohne options exakt Bestandsverhalten):
   *   - senderName:  der kundensichtbare Titel trägt den GEWÄHLTEN Absender
   *                  ("Datei Upload von {senderName} am {Datum}").
   *   - senderEmail: wird als sender_email persistiert (Signatur/Duz-Gate
   *                  der Versand-Mail; Grundlage für den Cron-Versand).
   *   - scheduledAtLocal: Berliner Wanduhrzeit → Paket wird als 'geplant'
   *                  angelegt (Konvertierung + Vergangenheits-Prüfung hier,
   *                  BEVOR irgendetwas angelegt wird); der Aufrufer löst
   *                  dann keine Sofort-Mail aus.
   */
  async createUploadWithFiles(
    customerId: string,
    contactId: string,
    files: File[],
    kommentar: string | null,
    erstelltVonEmail: string,
    erstelltVonName: string,
    options?: SecureUploadSendOptions
  ): Promise<{ success: boolean; uploadId?: string; error?: string }> {
    try {
      // 0. Stufe 2: Terminierung validieren/konvertieren, BEVOR irgendetwas
      //    angelegt oder verändert wird.
      let geplanterVersandAmIso: string | null = null;
      if (options?.scheduledAtLocal) {
        try {
          geplanterVersandAmIso = berlinLocalToUtcIso(options.scheduledAtLocal);
        } catch (e) {
          return { success: false, error: e instanceof Error ? e.message : 'Ungültiger Zeitpunkt' };
        }
        if (new Date(geplanterVersandAmIso).getTime() <= Date.now()) {
          return { success: false, error: 'Der geplante Zeitpunkt liegt in der Vergangenheit.' };
        }
      }

      // 1. Sicherstellen dass der Ansprechpartner einen Token hat
      const tokenResult = await customerContactService.ensureContactToken(contactId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      // 2. Titel generieren
      const now = new Date();
      const datumFormatiert = now.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      // Außenidentität „Senden als" (Stufe 2): der kundensichtbare Titel trägt
      // den GEWÄHLTEN Absender; ohne options wie bisher die handelnde Person.
      const anzeigeName = options?.senderName && options.senderName.trim()
        ? options.senderName.trim()
        : erstelltVonName;
      const autoTitel = `Datei Upload von ${anzeigeName} am ${datumFormatiert}`;
      // NEU (Überschrift): Wählt der Mitarbeiter im Modal eine Überschrift,
      // wird diese verwendet; sonst bleibt der automatische Titel als Fallback.
      const titel = options?.titel && options.titel.trim()
        ? options.titel.trim()
        : autoTitel;

      // 3. Upload-Eintrag erstellen
      const uploadResult = await this.createUpload({
        customer_id: customerId,
        contact_id: contactId,
        titel,
        kommentar,
        erstellt_von_email: erstelltVonEmail,
        erstellt_von_name: erstelltVonName,
        sender_email: options?.senderEmail && options.senderEmail.trim()
          ? options.senderEmail.trim()
          : undefined,
        geplanter_versand_am: geplanterVersandAmIso ?? undefined,
        // Punkt 2 (Veralten-Tracking): Periode + Paket-Typ durchreichen
        periode_jahr: options?.periodeJahr ?? undefined,
        periode_monat: options?.periodeMonat ?? undefined,
        paket_typ: options?.paketTyp ?? undefined
      });

      if (!uploadResult.success || !uploadResult.upload) {
        return { success: false, error: uploadResult.error };
      }

      const uploadId = uploadResult.upload.id;

      // 4. Alle Dateien hochladen
      const uploadErrors: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const fileResult = await this.uploadFile(uploadId, customerId, files[i], i);
        if (!fileResult.success) {
          uploadErrors.push(`${files[i].name}: ${fileResult.error}`);
        }
      }

      // Falls alle Dateien fehlgeschlagen sind, Upload löschen
      if (uploadErrors.length === files.length) {
        await this.deleteUpload(uploadId);
        return {
          success: false,
          error: `Alle Dateien konnten nicht hochgeladen werden:\n${uploadErrors.join('\n')}`
        };
      }

      // Bei teilweisem Erfolg: Warnung loggen, aber fortfahren
      if (uploadErrors.length > 0) {
        console.warn(`[SecureDocumentService] Partial upload failures:`, uploadErrors);
      }

      return {
        success: true,
        uploadId
      };

    } catch (error) {
      console.error('Error in createUploadWithFiles:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Löscht einen Upload und alle zugehörigen Dateien
   */
  async deleteUpload(uploadId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Dateien aus Storage laden
      const { data: files } = await supabase
        .from('secure_customer_upload_files')
        .select('storage_path')
        .eq('upload_id', uploadId);

      // 2. Dateien aus Storage löschen
      if (files && files.length > 0) {
        const paths = files.map(f => f.storage_path);
        await supabase.storage
          .from('secure-customer-documents')
          .remove(paths);
      }

      // 3. Upload löschen (CASCADE löscht auch die Datei-Einträge)
      const { error } = await supabase
        .from('secure_customer_uploads')
        .delete()
        .eq('id', uploadId);

      if (error) {
        return {
          success: false,
          error: `Upload löschen fehlgeschlagen: ${error.message}`
        };
      }

      return { success: true };

    } catch (error) {
      console.error('Error in deleteUpload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Stufe 2: Bricht ein GEPLANTES Paket ab und entfernt es vollständig
   * (Storage-Dateien + Zeilen). Zweiphasig gegen Race-Conditions mit dem
   * Versand-Cron (secure-scheduled-send):
   *   1. Bedingtes Update 'geplant' → 'entwurf' — greift nur, solange der
   *      Cron das Paket nicht geclaimt hat (versand_claimed_at IS NULL).
   *      Danach kann der Cron es nicht mehr aufnehmen (der Claim filtert
   *      auf versand_status='geplant').
   *   2. deleteUpload entfernt Storage und Zeilen (CASCADE).
   * Ist das Paket bereits geclaimt, versendet oder fehlgeschlagen, wird
   * nichts gelöscht und eine verständliche Fehlermeldung zurückgegeben.
   */
  async cancelScheduledUpload(uploadId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('secure_customer_uploads')
        .update({
          versand_status: 'entwurf',
          geplanter_versand_am: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', uploadId)
        .eq('versand_status', 'geplant')
        .is('versand_claimed_at', null)
        .select('id')
        .maybeSingle();

      if (error) {
        console.error('Error cancelling scheduled upload:', error);
        return {
          success: false,
          error: `Abbrechen fehlgeschlagen: ${error.message}`
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'Versand läuft bereits oder ist abgeschlossen — Abbrechen nicht mehr möglich.'
        };
      }

      return this.deleteUpload(uploadId);

    } catch (error) {
      console.error('Error in cancelScheduledUpload:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ============================================
  // LADEN / ABFRAGEN
  // ============================================

  /**
   * Lädt alle Uploads für einen Kunden (mit Dateien)
   */
  async getUploadsForCustomer(customerId: string): Promise<SecureUploadWithFiles[]> {
    try {
      // Uploads laden
      const { data: uploads, error: uploadsError } = await supabase
        .from('secure_customer_uploads')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (uploadsError || !uploads) {
        console.error('Error loading uploads:', uploadsError);
        return [];
      }

      // Dateien für jeden Upload laden
      const uploadsWithFiles: SecureUploadWithFiles[] = await Promise.all(
        uploads.map(async (upload) => {
          const { data: files } = await supabase
            .from('secure_customer_upload_files')
            .select('*')
            .eq('upload_id', upload.id)
            .order('sort_order', { ascending: true });

          return {
            ...upload,
            files: (files || []) as SecureCustomerUploadFile[]
          };
        })
      );

      return uploadsWithFiles;

    } catch (error) {
      console.error('Error in getUploadsForCustomer:', error);
      return [];
    }
  }

  /**
   * Lädt alle versendeten Uploads für einen bestimmten Ansprechpartner (mit Dateien)
   * Zeigt exakt dieselben Daten wie die öffentliche Kunden-Download-Seite
   */
  async getUploadsForContact(contactId: string): Promise<SecureUploadWithFiles[]> {
    try {
      // Uploads laden - nur versendete, isoliert pro Ansprechpartner
      const { data: uploads, error: uploadsError } = await supabase
        .from('secure_customer_uploads')
        .select('*')
        .eq('contact_id', contactId)
        .eq('versand_status', 'versendet')
        .order('created_at', { ascending: false });

      if (uploadsError || !uploads) {
        console.error('Error loading uploads for contact:', uploadsError);
        return [];
      }

      // Dateien für jeden Upload laden
      const uploadsWithFiles: SecureUploadWithFiles[] = await Promise.all(
        uploads.map(async (upload) => {
          const { data: files } = await supabase
            .from('secure_customer_upload_files')
            .select('*')
            .eq('upload_id', upload.id)
            .order('sort_order', { ascending: true });

          return {
            ...upload,
            files: (files || []) as SecureCustomerUploadFile[]
          };
        })
      );

      return uploadsWithFiles;

    } catch (error) {
      console.error('Error in getUploadsForContact:', error);
      return [];
    }
  }

  /**
   * Stufe 2: Lädt geplante und fehlgeschlagene Uploads eines Ansprechpartners
   * (mit Dateien) für den Abschnitt "Geplante Sendungen" im Downloadbereich-Tab
   * (Mitarbeiter-Sicht). Bewusst getrennt von getUploadsForContact — die
   * Kundensicht-Vorschau zeigt weiterhin ausschließlich versendete Pakete.
   * Sortierung: nächster Versandzeitpunkt zuerst; Pakete ohne Zeitpunkt
   * (historische Sofort-Versand-Fehler) danach, jüngste zuerst.
   * Explizites .limit() wegen Supabase-Default-Row-Limit von 1000.
   */
  async getScheduledUploadsForContact(contactId: string): Promise<SecureUploadWithFiles[]> {
    try {
      const { data: uploads, error: uploadsError } = await supabase
        .from('secure_customer_uploads')
        .select('*')
        .eq('contact_id', contactId)
        .in('versand_status', ['geplant', 'fehler'])
        .order('geplanter_versand_am', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(100000);

      if (uploadsError || !uploads) {
        console.error('Error loading scheduled uploads for contact:', uploadsError);
        return [];
      }

      // Dateien für jeden Upload laden
      const uploadsWithFiles: SecureUploadWithFiles[] = await Promise.all(
        uploads.map(async (upload) => {
          const { data: files } = await supabase
            .from('secure_customer_upload_files')
            .select('*')
            .eq('upload_id', upload.id)
            .order('sort_order', { ascending: true });

          return {
            ...upload,
            files: (files || []) as SecureCustomerUploadFile[]
          };
        })
      );

      return uploadsWithFiles;

    } catch (error) {
      console.error('Error in getScheduledUploadsForContact:', error);
      return [];
    }
  }

  /**
   * Lädt einen einzelnen Upload mit Dateien
   */
  async getUploadById(uploadId: string): Promise<SecureUploadWithFiles | null> {
    try {
      const { data: upload, error } = await supabase
        .from('secure_customer_uploads')
        .select('*')
        .eq('id', uploadId)
        .single();

      if (error || !upload) {
        return null;
      }

      const { data: files } = await supabase
        .from('secure_customer_upload_files')
        .select('*')
        .eq('upload_id', uploadId)
        .order('sort_order', { ascending: true });

      return {
        ...upload,
        files: (files || []) as SecureCustomerUploadFile[]
      };

    } catch (error) {
      console.error('Error in getUploadById:', error);
      return null;
    }
  }

  /**
   * NEU (Zugriffsprotokoll): Lädt das Zugriffsprotokoll (Logins und
   * Downloads) für einen Ansprechpartner, chronologisch absteigend.
   * Explizites .limit() wegen Supabase-Default-Row-Limit von 1000.
   */
  async getAccessLogForContact(contactId: string): Promise<SecureAccessLogEntry[]> {
    try {
      const { data, error } = await supabase
        .from('secure_customer_access_log')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(100000);

      if (error || !data) {
        console.error('Error loading access log for contact:', error);
        return [];
      }

      return data as SecureAccessLogEntry[];

    } catch (error) {
      console.error('Error in getAccessLogForContact:', error);
      return [];
    }
  }

  // ============================================
  // VERSAND
  // ============================================

  /**
   * Markiert einen Upload als versendet
   */
  async markAsSent(
    uploadId: string,
    empfaengerEmail: string,
    empfaengerCc: string | null
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('secure_customer_uploads')
        .update({
          versand_status: 'versendet',
          versendet_am: new Date().toISOString(),
          empfaenger_email: empfaengerEmail,
          empfaenger_cc: empfaengerCc,
          updated_at: new Date().toISOString()
        })
        .eq('id', uploadId);

      if (error) {
        return {
          success: false,
          error: `Status-Update fehlgeschlagen: ${error.message}`
        };
      }

      return { success: true };

    } catch (error) {
      console.error('Error in markAsSent:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Markiert einen Upload als fehlerhaft
   */
  async markAsError(uploadId: string, fehlerNachricht: string): Promise<void> {
    try {
      await supabase
        .from('secure_customer_uploads')
        .update({
          versand_status: 'fehler',
          fehler_nachricht: fehlerNachricht,
          updated_at: new Date().toISOString()
        })
        .eq('id', uploadId);
    } catch (error) {
      console.error('Error in markAsError:', error);
    }
  }

  /**
   * Erstellt einen Versand-Log-Eintrag
   */
  async createSendLog(
    uploadId: string,
    empfaenger: string,
    cc: string | null,
    betreff: string,
    erfolg: boolean,
    fehler?: string
  ): Promise<void> {
    try {
      await supabase
        .from('secure_customer_send_log')
        .insert({
          upload_id: uploadId,
          email_empfaenger: empfaenger,
          email_cc: cc,
          email_betreff: betreff,
          versand_erfolg: erfolg,
          fehler_details: fehler || null
        });
    } catch (error) {
      console.error('Error in createSendLog:', error);
    }
  }

  /**
   * Generiert eine signierte Download-URL für eine Datei.
   *
   * Optional kann ein filename übergeben werden. Dann wird via Supabase
   * download-Option der Content-Disposition: attachment Header serverseitig
   * gesetzt, sodass der Browser die Datei herunterlädt statt sie inline
   * anzuzeigen (z.B. XML-Quelltext in neuem Tab). Wirkt unabhängig von
   * Cross-Origin.
   */
  async getSignedDownloadUrl(storagePath: string, filename?: string): Promise<string | null> {
    try {
      const options = filename ? { download: filename } : undefined;
      const { data, error } = await supabase.storage
        .from('secure-customer-documents')
        .createSignedUrl(storagePath, 3600, options); // 1 Stunde gültig

      if (error || !data) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;

    } catch (error) {
      console.error('Error in getSignedDownloadUrl:', error);
      return null;
    }
  }
}

// Singleton Export
export const secureDocumentService = new SecureDocumentService();
